// src/hooks/useAuth.js
import { createContext, useContext, useState, useEffect } from 'react';
import {
  signIn,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  autoSignIn,
  confirmSignIn
} from 'aws-amplify/auth';
import { syncUserAttributes } from '../utils/userAttributesHelper';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check for authenticated user on load
  useEffect(() => {
    const checkAuthState = async () => {
      try {
        // Try auto sign-in first
        try {
          const autoSignInResult = await autoSignIn();
          if (autoSignInResult.isSignedIn) {
            console.log('Auto sign-in successful');
          }
        } catch (autoSignInError) {
          // Auto sign-in is not always available, this is normal
          // Only log if it's not the expected "no credentials" error
          if (autoSignInError.name !== 'AuthTokenConfigException') {
            console.log('Auto sign-in error:', autoSignInError.message);
          }
        }
        
        // Try to get current authenticated user
        const currentUser = await getCurrentUser();
        
        if (currentUser) {
          console.log('Found authenticated user:', currentUser);
          
          // Get session to extract groups
          const session = await fetchAuthSession();
          
          if (!session) {
            throw new Error('Unable to fetch auth session');
          }
          
          console.log('Auth session:', session);
          
          // Extract user groups from cognito:groups claim if available
          let userGroups = [];
          
          if (session.tokens?.idToken?.payload['cognito:groups']) {
            userGroups = session.tokens.idToken.payload['cognito:groups'];
          }
          
          console.log('User groups:', userGroups);
          
          setUser(currentUser);
          setGroups(userGroups);

          // Sync user attributes on initial load - but only for non-admin users
          // Admins don't need student profile sync
          if (!userGroups.includes('Admin')) {
            syncUserAttributes().catch(err => {
              console.log('Failed to sync user attributes on init:', err);
            });
          }
        } else {
          console.log('No authenticated user found');
          setUser(null);
          setGroups([]);
        }
      } catch (error) {
        // Don't log errors for unauthenticated users - this is expected
        if (error.name !== 'UserUnAuthenticatedException') {
          console.error('Error checking auth state:', error);
        }
        setUser(null);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthState();
  }, []);

  const handleSignIn = async (username, password) => {
    try {
      console.log('Signing in user:', username);
      
      // Verify Auth is configured before attempting sign in
      try {
        const testSession = await fetchAuthSession();
        console.log('Auth configuration verified, proceeding with sign in');
      } catch (configError) {
        console.error('Auth not properly configured:', configError);
        throw new Error('Authentication service is not properly configured. Please refresh the page and try again.');
      }
      
      // Sign in with Amplify
      const signInResult = await signIn({
        username,
        password,
      });
      
      console.log('Sign in result:', signInResult);

      if (signInResult.isSignedIn) {
        return await finalizeSignIn();
      }

      // First login for admin-created (invited) users: Cognito requires a new password.
      const step = signInResult.nextStep?.signInStep;
      if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { isSignedIn: false, nextStep: 'NEW_PASSWORD_REQUIRED' };
      }

      // Any other incomplete step (MFA, etc.) — surface it so the UI can decide.
      throw new Error(`Sign in incomplete: ${step || 'unknown step'}`);
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  // Shared finalize: load the current user + groups after a fully-signed-in state.
  const finalizeSignIn = async () => {
    const currentUser = await getCurrentUser();
    const session = await fetchAuthSession();

    let userGroups = [];
    if (session.tokens?.idToken?.payload['cognito:groups']) {
      userGroups = session.tokens.idToken.payload['cognito:groups'];
    }

    setUser(currentUser);
    setGroups(userGroups);

    if (!userGroups.includes('Admin')) {
      syncUserAttributes().catch(err => console.log('Failed to sync user attributes:', err));
    }

    return { isSignedIn: true, userGroups };
  };

  // Complete the forced-password-change challenge for first-time (invited) users.
  const completeNewPassword = async (newPassword) => {
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      if (result.isSignedIn) {
        return await finalizeSignIn();
      }
      throw new Error(`Password change incomplete: ${result.nextStep?.signInStep || 'unknown step'}`);
    } catch (error) {
      console.error('Complete new password error:', error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      console.log('Signing out user');
      await signOut();
      console.log('User signed out successfully');
      
      setUser(null);
      setGroups([]);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const value = {
    user,
    groups,
    loading,
    signIn: handleSignIn,
    completeNewPassword,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}